/**
 * Module dependencies.
 */

import {
  ReactNode,
  RefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

/**
 * `StateDelay` type.
 */

export type StateDelay = {
  delay: number;
  hasEntered: boolean;
};

/**
 * `Item` type.
 */

type Item = {
  delay: number;
  onEnter: (delay: number) => void;
  ref: RefObject<HTMLElement | null>;
};

/**
 * `ContextType` type.
 */

type ContextType = {
  register: (item: Item) => void;
  unregister: (ref: RefObject<HTMLElement | null>) => void;
};

/**
 * `AnimationDelayContext` context.
 */

const AnimationDelayContext = createContext<ContextType | null>(null);

/**
 * `AnimationDelayProvider` component.
 */

export function AnimationDelayProvider({ children }: { children: ReactNode }) {
  const entriesRef = useRef<number[]>([]);
  const itemsRef = useRef<Item[]>([]);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const getDelay = useCallback((delayUnit: number): number => {
    const now = performance.now();

    entriesRef.current = entriesRef.current.filter(
      (startTime) => now - startTime < delayUnit,
    );

    const delay = entriesRef.current.length * delayUnit;

    entriesRef.current.push(now);

    return delay;
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const item = itemsRef.current.find(
              (i) => i.ref.current === entry.target,
            );

            if (item) {
              const delay = getDelay(item.delay);

              item.onEnter(delay);
              observerRef.current?.unobserve(entry.target);
              itemsRef.current = itemsRef.current.filter((i) => i !== item);
            }
          }
        });
      },
      { threshold: 0.5 },
    );

    return () => observerRef.current?.disconnect();
  }, [getDelay]);

  const register = useCallback((item: Item) => {
    if (!observerRef.current) {
      const timer = setTimeout(() => {
        clearTimeout(timer);
        register(item);
      }, 10);

      return;
    }

    itemsRef.current.push(item);

    if (item.ref.current) {
      observerRef.current?.observe(item.ref.current);
    }
  }, []);

  const unregister = useCallback((ref: RefObject<HTMLElement | null>) => {
    itemsRef.current = itemsRef.current.filter((i) => i.ref !== ref);
    if (ref.current) observerRef.current?.unobserve(ref.current);
  }, []);

  return (
    <AnimationDelayContext.Provider value={{ register, unregister }}>
      {children}
    </AnimationDelayContext.Provider>
  );
}

/**
 * `useAnimationDelayObserver` hook.
 */

export function useAnimationDelayObserver() {
  const ctx = useContext(AnimationDelayContext);

  if (!ctx) {
    throw new Error(
      'useAnimationDelayObserver must be used inside AnimationDelayProvider',
    );
  }

  return ctx;
}

/**
 * `useAnimationDelayItem` hook.
 */

export function useAnimationDelayItem(delay = 150) {
  const ref = useRef<HTMLElement>(null);
  const [state, setState] = useState<StateDelay>({
    delay: 0,
    hasEntered: false,
  });

  const { register, unregister } = useAnimationDelayObserver();

  useEffect(() => {
    register({
      delay,
      onEnter: (delay) => {
        setState({ delay, hasEntered: true });
      },
      ref,
    });

    return () => {
      unregister(ref);
    };
  }, [delay, register, unregister]);

  return { ref, stateDelay: state };
}
